import {
	AfterViewInit,
	Component,
	ElementRef,
	Input,
	OnDestroy,
	OnInit,
	ViewChild,
} from '@angular/core';
import { Label, LanguageService } from '@services/public';
import { Select, SelectKey } from '@util';
import { Chart, ChartConfiguration } from 'chart.js';
import { BsModalService } from 'ngx-bootstrap/modal';
import { Observable, Subscription } from 'rxjs';

@Component({
	selector: 'app-chart',
	templateUrl: './chart.component.html',
	styleUrls: ['./chart.component.scss'],
})
export class ChartComponent implements OnInit, AfterViewInit, OnDestroy {
	VIEW_ALL_FILTER_KEY: string = SelectKey.VIEW_ALL;

	@ViewChild('chartContainer', { read: ElementRef }) chartContainer: ElementRef;
	@ViewChild('chart', { read: ElementRef }) chart: ElementRef;

	@Input() title: string = '';
	@Input() filters: Array<Select<any>> = [];
	@Input() chartConfiguration: Observable<ChartConfiguration>;
	@Input() info: string = null;

	private _chart: Chart;
	private _chartSubscription: Subscription = new Subscription();

	// Page langauge.
	labels: { [key in Label]?: string } = {
		[Label.VIEW_ALL]: '',
	};

	constructor(
		private _languageSvc: LanguageService,
		private _modalSvc: BsModalService,
	) {}

	ngOnInit(): void {
		// Get page language.
		this._languageSvc.getLabels(this.labels);
	}

	ngAfterViewInit(): void {
		this._subscribeToChart();
	}

	get showViewAll(): boolean {
		return (
			(this.filters.find((filter) => filter.key === SelectKey.VIEW_ALL)
				?.value ?? null) !== null
		);
	}

	/**
	 * Subscribe to the chart configuration observable.
	 */
	private _subscribeToChart() {
		this._chartSubscription = this.chartConfiguration.subscribe(
			(configuration: ChartConfiguration) => {
				if (!configuration) return;
				if (!!this._chart) this._chart.destroy();
				if (!this.filters.find((filter) => filter.key === SelectKey.VIEW_ALL)) {
					this.chartContainer.nativeElement.style.height = `${
						configuration.data.labels.length * 25 + 170
					}px`;
				}
				this._chart = new Chart(this.chart.nativeElement, configuration);
			},
		);
	}

	viewAll(): void {
		// Disable chart updates.
		this._chartSubscription.unsubscribe();

		// Set view all to true.
		const filter = this.filters.find(
			(filter) => filter.key === SelectKey.VIEW_ALL,
		);
		if (!!filter) filter.value = true;

		// Display modal.
		const subscription = this._modalSvc
			.show(ChartComponent, {
				class: 'modal-md modal-dialog-centered',
				backdrop: true,
				keyboard: true,
				initialState: {
					title: this.title,
					filters: this.filters.filter(
						(filter) => filter.key !== SelectKey.VIEW_ALL,
					),
					chartConfiguration: this.chartConfiguration,
					info: this.info,
				},
			})
			.onHidden.subscribe(
				(() => {
					subscription.unsubscribe();

					// Set view all to false.
					if (!!filter) filter.value = false;

					this._subscribeToChart();
				}).bind(this),
			);
	}

	onClose(): void {
		this._modalSvc.hide();
	}

	ngOnDestroy(): void {
		this._chartSubscription.unsubscribe();
	}
}
